import { Container } from "pixi.js";
import type { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { ReelAnticipationFrame } from "@slot/mechanics-reels";
import type { PresentationTaskRunner } from "@slot/presentation";

/**
 * Game-side anticipation-frame view — `AnticipationFrame.prefab` + its
 * `AnticipationFrameView`. The reel mechanic drives it via playIn()/playOut(); the animation is
 * GAME-OWNED: an alpha fade in/out on the CHROME runner (constant perceived speed).
 *
 * Tree (from the prefab dump): root 187.7×705 > `vfx_board` Spine at (0, 21.49 y-up) scaled (0.499, 0.87),
 * looping its `fast` clip.
 */
export class AnticipationFrameView implements ReelAnticipationFrame {
  public readonly node: Container;
  public fadeInSeconds = 0.2;
  public fadeOutSeconds = 0.2;
  private readonly runner: PresentationTaskRunner;
  private generation = 0;

  public constructor(input: { spine: Spine | null; chromeRunner: PresentationTaskRunner }) {
    this.runner = input.chromeRunner;
    this.node = new Container({ label: "AnticipationFrame" });
    this.node.alpha = 0;
    this.node.visible = false;
    if (input.spine) {
      input.spine.position.set(0, -21.49);
      input.spine.scale.set(0.499, 0.87);
      if (input.spine.skeleton.data.findAnimation("fast")) input.spine.state.setAnimation(0, "fast", true);
      this.node.addChild(input.spine);
    }
  }

  public playIn(): void {
    this.node.visible = true;
    void this.fadeTo(1, this.fadeInSeconds);
  }

  public playOut(): void {
    void this.fadeTo(0, this.fadeOutSeconds).then((completed) => {
      if (completed) this.node.visible = false;
    });
  }

  private async fadeTo(target: number, seconds: number): Promise<boolean> {
    const gen = ++this.generation;
    const node = this.node;
    const from = node.alpha;
    const scaled = Math.max(0, seconds);
    if (scaled <= 0) {
      node.alpha = target;
      return true;
    }
    await this.runner.tweenProgress(scaled, (k) => {
      if (gen === this.generation && !node.destroyed) node.alpha = from + (target - from) * k;
    });
    return gen === this.generation;
  }
}
